import { useEffect, useRef } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

const getFirstErrorPath = (errors: unknown, prefix = ''): string | null => {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  const typedErrors = errors as Record<string, unknown>;
  const keys = Object.keys(typedErrors);

  if (keys.length === 0) {
    return null;
  }

  for (const key of keys) {
    if (
      key === 'message' ||
      key === 'type' ||
      key === 'ref' ||
      key === 'root'
    ) {
      continue;
    }

    const value = typedErrors[key];
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object') {
      const nestedPath = getFirstErrorPath(value, path);
      if (nestedPath) {
        return nestedPath;
      }
    }
  }

  return prefix || keys[0] || null;
};

export const useScrollToError = <T extends FieldValues>() => {
  const { formState } = useFormContext<T>();
  const previousSubmitCountRef = useRef<number>(0);

  const isVisible = (element: Element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element instanceof HTMLInputElement && element.type === 'hidden') {
      return false;
    }
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    // Hidden elements tend to have no client rects
    return element.getClientRects().length > 0;
  };

  useEffect(() => {
    const isNewSubmitAttempt =
      formState.submitCount > previousSubmitCountRef.current;
    previousSubmitCountRef.current = formState.submitCount;

    // Scroll only on a new submit attempt, not on every error object update.
    if (isNewSubmitAttempt && Object.keys(formState.errors).length > 0) {
      const firstErrorPath =
        getFirstErrorPath(formState.errors) ?? Object.keys(formState.errors)[0];
      if (!firstErrorPath) {
        return;
      }

      const topLevelField = firstErrorPath.split('.')[0];
      const elementByName = document.querySelector(
        `[name="${firstErrorPath}"]`
      );
      const elementByErrorAnchor = document.querySelector(
        `[data-error-for="${firstErrorPath}"]`
      );
      const topLevelAnchors = document.querySelectorAll(
        `[data-error-for="${topLevelField}"]`
      );
      const elementByTopLevelAnchor =
        topLevelAnchors.length > 0
          ? topLevelAnchors[topLevelAnchors.length - 1]
          : null;

      // Prefer a visible anchor over a hidden input
      const element =
        (elementByName && isVisible(elementByName)
          ? elementByName
          : undefined) ??
        elementByErrorAnchor ??
        elementByTopLevelAnchor;
      if (!element) return;

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // If the target is focusable, focus it (helps screen readers and keyboard users)
      if (element instanceof HTMLElement) {
        element.focus?.({ preventScroll: true });
      }
    }
  }, [formState.errors, formState.submitCount]);
};
